import { useCallback, useEffect, type MutableRefObject, type Dispatch, type SetStateAction } from "react";
import { isLastFieldInForm } from "../utils/isLastFieldInForm";
import { navigateToNextField, submitForm } from "./useSelectKeyboard.helpers";
import type { SelectOption } from "./useSelectState.types";

const ARROW_KEY_MAP: Record<string, 'up' | 'down'> = { ArrowDown: 'down', ArrowUp: 'up' };

/**
 * Cancels any pending navigation/blur animation frames on unmount to avoid
 * running callbacks against a torn-down component.
 *
 * @param navigationRef Ref holding the pending navigation frame id.
 * @param blurRef Ref holding the pending blur frame id.
 */
export function useCleanupRefs(
  navigationRef: MutableRefObject<number | null>,
  blurRef: MutableRefObject<number | null>,
): void {
  useEffect(() => {
    return () => {
      if (navigationRef.current !== null) cancelAnimationFrame(navigationRef.current);
      if (blurRef.current !== null) cancelAnimationFrame(blurRef.current);
    };
  }, []);
}

/**
 * Builds the action to run after the select blurs: advance to the next field,
 * submit the form when it is the last field, or move focus onward.
 *
 * @param onNextField Optional explicit next-field callback.
 * @param name The field name used for last-field detection.
 * @param onSubmit Optional submit callback used on the last field.
 * @param navigationTimeoutRef Ref tracking the navigation frame id.
 * @returns A resolver returning the post-blur action for an input.
 */
export function useResolvePostBlurAction(
  onNextField: (() => void) | undefined,
  name: string,
  onSubmit: (() => void) | undefined,
  navigationTimeoutRef: MutableRefObject<number | null>,
) {
  return useCallback((input: HTMLInputElement): (() => void) => {
    if (onNextField) {
      return () => {
        onNextField();
        if (!isLastFieldInForm(name)) navigateToNextField(input, navigationTimeoutRef);
      };
    }
    if (isLastFieldInForm(name)) return () => submitForm(onSubmit);
    return () => navigateToNextField(input, navigationTimeoutRef);
  }, [onNextField, name, onSubmit]);
}

/**
 * Blurs the input on the next animation frame, clears focus state, then runs
 * the resolved post-blur action; cancels any previously scheduled frame.
 *
 * @param blurTimeoutRef Ref tracking the pending blur frame id.
 * @param resolvePostBlurAction Resolver producing the action to run after blur.
 * @returns A callback that schedules the deferred blur handling.
 */
export function useHandleNavigationAfterBlur(
  blurTimeoutRef: MutableRefObject<number | null>,
  resolvePostBlurAction: (input: HTMLInputElement) => (() => void),
) {
  return useCallback((input: HTMLInputElement, setIsFocused: (v: boolean) => void) => {
    if (blurTimeoutRef.current !== null) cancelAnimationFrame(blurTimeoutRef.current);
    blurTimeoutRef.current = requestAnimationFrame(() => {
      input.blur();
      setIsFocused(false);
      resolvePostBlurAction(input)();
      blurTimeoutRef.current = null;
    });
  }, [resolvePostBlurAction]);
}

/**
 * Handles Enter when the dropdown has options: selects the active (or first)
 * option, syncs its display name into the query, then defers blur handling.
 *
 * @param shouldSkipSelection Predicate that bypasses selection when true.
 * @param filteredOptions The currently visible option values.
 * @param activeOptionIndex Index of the highlighted option.
 * @param ensureDisplayNameInQuery Syncs the chosen value's label into the query.
 * @param onSelect Selection callback.
 * @param handleNavigationAfterBlur Deferred blur/navigation handler.
 * @returns An Enter-key handler for the options case.
 */
export function useHandleEnterWithOptions(
  shouldSkipSelection: () => boolean,
  filteredOptions: string[],
  activeOptionIndex: number,
  ensureDisplayNameInQuery: (v: string) => void,
  onSelect: (v: string | null) => void,
  handleNavigationAfterBlur: (input: HTMLInputElement, setIsFocused: (v: boolean) => void) => void,
) {
  return useCallback((event: React.KeyboardEvent<HTMLInputElement>, setIsFocused: (v: boolean) => void) => {
    event.preventDefault();
    if (shouldSkipSelection()) {
      handleNavigationAfterBlur(event.target as HTMLInputElement, setIsFocused);
      return;
    }
    const optionToSelect = filteredOptions[activeOptionIndex] || filteredOptions[0];
    ensureDisplayNameInQuery(optionToSelect);
    onSelect(optionToSelect);
    handleNavigationAfterBlur(event.target as HTMLInputElement, setIsFocused);
  }, [shouldSkipSelection, filteredOptions, activeOptionIndex, ensureDisplayNameInQuery, onSelect, handleNavigationAfterBlur]);
}

/**
 * Handles Enter when a value is already selected: re-confirms the selection,
 * syncs its display name into the query, then defers blur handling.
 *
 * @param selectedValue The currently selected option value.
 * @param ensureDisplayNameInQuery Syncs the value's label into the query.
 * @param onSelect Selection callback.
 * @param handleNavigationAfterBlur Deferred blur/navigation handler.
 * @returns An Enter-key handler for the pre-selected case.
 */
export function useHandleEnterWithSelectedValue(
  selectedValue: string,
  ensureDisplayNameInQuery: (v: string) => void,
  onSelect: (v: string | null) => void,
  handleNavigationAfterBlur: (input: HTMLInputElement, setIsFocused: (v: boolean) => void) => void,
) {
  return useCallback((event: React.KeyboardEvent<HTMLInputElement>, setIsFocused: (v: boolean) => void) => {
    event.preventDefault();
    ensureDisplayNameInQuery(selectedValue);
    onSelect(selectedValue);
    handleNavigationAfterBlur(event.target as HTMLInputElement, setIsFocused);
  }, [selectedValue, ensureDisplayNameInQuery, onSelect, handleNavigationAfterBlur]);
}

/**
 * Handles Enter with no selectable option: submits the form on the last field,
 * otherwise advances focus to the next field.
 *
 * @param name The field name used for last-field detection.
 * @param onSubmit Optional submit callback.
 * @param onNextField Optional next-field callback.
 * @param navigationTimeoutRef Ref tracking the navigation frame id.
 * @returns The default Enter-key handler.
 */
export function useHandleEnterDefault(
  name: string,
  onSubmit: (() => void) | undefined,
  onNextField: (() => void) | undefined,
  navigationTimeoutRef: MutableRefObject<number | null>,
) {
  return useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    if (isLastFieldInForm(name)) { submitForm(onSubmit); return; }
    if (onNextField) onNextField();
    navigateToNextField(input, navigationTimeoutRef);
  }, [name, onSubmit, onNextField]);
}

/**
 * Dispatches Enter to the options, selected-value, or default handler based on
 * the current selection predicates.
 *
 * @param shouldSelectFromOptions True when an option should be picked.
 * @param shouldSelectExistingValue True when the existing value applies.
 * @param handleEnterWithOptions Handler for the options case.
 * @param handleEnterWithSelectedValue Handler for the pre-selected case.
 * @param handleEnterDefault Fallback Enter handler.
 * @returns The combined Enter-key handler.
 */
export function useHandleEnterKey(
  shouldSelectFromOptions: () => boolean,
  shouldSelectExistingValue: () => boolean,
  handleEnterWithOptions: (e: React.KeyboardEvent<HTMLInputElement>, s: (v: boolean) => void) => void,
  handleEnterWithSelectedValue: (e: React.KeyboardEvent<HTMLInputElement>, s: (v: boolean) => void) => void,
  handleEnterDefault: (e: React.KeyboardEvent<HTMLInputElement>) => void,
) {
  return useCallback((event: React.KeyboardEvent<HTMLInputElement>, setIsFocused: (v: boolean) => void) => {
    if (shouldSelectFromOptions()) { handleEnterWithOptions(event, setIsFocused); return; }
    if (shouldSelectExistingValue()) { handleEnterWithSelectedValue(event, setIsFocused); return; }
    handleEnterDefault(event);
  }, [shouldSelectFromOptions, shouldSelectExistingValue, handleEnterWithOptions, handleEnterWithSelectedValue, handleEnterDefault]);
}

/**
 * Moves the active option index up or down with wrap-around, marking that
 * keyboard navigation is in use.
 *
 * @param filteredOptions The currently visible option values.
 * @param setActiveOptionIndex Setter for the active index.
 * @param hasKeyboardNavigationRef Ref flagging keyboard-driven navigation.
 * @returns A directional navigation callback.
 */
export function useHandleArrowKeyNavigation(
  filteredOptions: string[],
  setActiveOptionIndex: Dispatch<SetStateAction<number>>,
  hasKeyboardNavigationRef: MutableRefObject<boolean>,
) {
  return useCallback((direction: 'up' | 'down') => {
    if (filteredOptions.length === 0) return;
    hasKeyboardNavigationRef.current = true;
    setActiveOptionIndex((cur) => {
      if (direction === 'down') return cur < filteredOptions.length - 1 ? cur + 1 : 0;
      return cur > 0 ? cur - 1 : filteredOptions.length - 1;
    });
  }, [filteredOptions, setActiveOptionIndex]);
}

/**
 * Handles keydown inside the searchable dropdown input: arrows move the active
 * row, Enter selects it, Escape closes the dropdown.
 *
 * @param filteredData The currently visible option data.
 * @param activeOptionIndex Index of the highlighted row.
 * @param close Optional close callback for Escape.
 * @param onSelect Selection callback.
 * @param setActiveOptionIndex Setter for the active index.
 * @returns The search-input keydown handler.
 */
export function useHandleSearchKeyDown(
  filteredData: SelectOption[] | undefined,
  activeOptionIndex: number,
  close: (() => void) | undefined,
  onSelect: (v: string | null) => void,
  setActiveOptionIndex: Dispatch<SetStateAction<number>>,
) {
  return useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const data = filteredData ?? [];
    const direction = ARROW_KEY_MAP[e.key];
    if (direction) { handleSearchArrow(e, direction, data.length, setActiveOptionIndex); return; }
    if (e.key === 'Enter') { handleSearchEnter(e, data, activeOptionIndex, onSelect); return; }
    if (e.key === 'Escape') { e.preventDefault(); close?.(); }
  }, [filteredData, activeOptionIndex, close, onSelect, setActiveOptionIndex]);
}

/**
 * Moves the active search row up or down with wrap-around in response to an
 * arrow key, preventing the default caret movement.
 *
 * @param e The keyboard event.
 * @param direction The navigation direction.
 * @param length The number of visible rows.
 * @param setActiveOptionIndex Setter for the active index.
 */
function handleSearchArrow(
  e: React.KeyboardEvent<HTMLInputElement>, direction: 'up' | 'down',
  length: number, setActiveOptionIndex: Dispatch<SetStateAction<number>>,
): void {
  e.preventDefault();
  setActiveOptionIndex((prev) => {
    if (direction === 'down') return prev + 1 >= length ? 0 : prev + 1;
    return prev - 1 < 0 ? length - 1 : prev - 1;
  });
}

/**
 * Selects the active (or first) row on Enter within the search input, guarding
 * against an empty or out-of-range index.
 *
 * @param e The keyboard event.
 * @param data The visible option data.
 * @param activeOptionIndex Index of the highlighted row.
 * @param onSelect Selection callback.
 */
function handleSearchEnter(
  e: React.KeyboardEvent<HTMLInputElement>, data: SelectOption[],
  activeOptionIndex: number, onSelect: (v: string | null) => void,
): void {
  e.preventDefault();
  if (data.length === 0) return;
  const idx = activeOptionIndex >= 0 && activeOptionIndex < data.length ? activeOptionIndex : 0;
  onSelect(data[idx].value);
}

/**
 * Builds the top-level keydown handler: Enter delegates to `handleEnterKey`, and
 * mapped arrow keys drive option navigation when focused with options present.
 *
 * @param handleEnterKey Handler for the Enter key.
 * @param isFocused Whether the search input is currently focused.
 * @param filteredOptionsLength Number of currently visible options.
 * @param handleArrowKeyNavigation Handler advancing the active option.
 * @returns A memoized keydown event handler.
 */
export function useHandleKeyDown(
  handleEnterKey: (e: React.KeyboardEvent<HTMLInputElement>, s: (v: boolean) => void) => void,
  isFocused: boolean,
  filteredOptionsLength: number,
  handleArrowKeyNavigation: (d: 'up' | 'down') => void,
) {
  return useCallback((event: React.KeyboardEvent<HTMLInputElement>, setIsFocused: (v: boolean) => void) => {
    if (event.key === 'Enter') { handleEnterKey(event, setIsFocused); return; }
    const direction = ARROW_KEY_MAP[event.key];
    if (!direction || !isFocused || filteredOptionsLength === 0) return;
    event.preventDefault();
    handleArrowKeyNavigation(direction);
  }, [handleEnterKey, isFocused, filteredOptionsLength, handleArrowKeyNavigation]);
}
